// ===================================================================
// KAIXO - Scanner de QR para vincular web
// ===================================================================
// Abre cámara con getUserMedia, escanea QR cada 250ms, busca pattern
// "kaixo://link/<TOKEN>" o el TOKEN suelto. Cuando lo encuentra:
//   1. POST /api/web-link/redeem  → recibe deviceInfo del navegador web
//   2. Modal de confirmación: "¿vincular este navegador con tu cuenta?"
//   3. Si confirma → POST /api/web-link/confirm → cerrar y notificar OK
// Fallback: input manual para teclear los 6 caracteres si la cámara no
// está disponible (permisos denegados, navegador antiguo).
// ===================================================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import jsQR from 'jsqr';
import api from '../../services/api';
import './WebLinkScanner.css';

// i18n local — strings de la UI de pairing móvil↔web. Mismo patrón que
// CallManager: lee idioma de kaixo_user en localStorage. 7 idiomas + EN
// fallback.
const WLS_I18N = {
    español: {
        title: 'Vincular web',
        close: 'Cerrar',
        scanInstructions: 'Apunta la cámara al QR que aparece en kaixo.ai/web',
        starting: 'Iniciando cámara…',
        manualBtn: 'Introducir código manualmente',
        manualTitle: 'Introduce el código de 6 caracteres',
        validating: 'Validando…',
        continue: 'Continuar',
        useCamera: '← Usar cámara',
        confirmTitle: '¿Vincular este navegador?',
        browserLabel: 'Navegador',
        ipLabel: 'IP',
        warning: 'Solo confirma si eres tú quien tiene esta web abierta. Tras vincular, ese navegador podrá acceder a tu cuenta hasta que cierres sesión.',
        cancel: 'Cancelar',
        linking: 'Vinculando…',
        confirmYes: 'Sí, vincular',
        successTitle: 'Vinculado correctamente',
        successDesc: 'El navegador ya tiene acceso a tu cuenta.',
        invalidCode: 'Código inválido (6 caracteres letras/números)',
        unknown: 'Desconocido'
    },
    inglés: {
        title: 'Link web',
        close: 'Close',
        scanInstructions: 'Point the camera at the QR shown on kaixo.ai/web',
        starting: 'Starting camera…',
        manualBtn: 'Enter code manually',
        manualTitle: 'Enter the 6-character code',
        validating: 'Validating…',
        continue: 'Continue',
        useCamera: '← Use camera',
        confirmTitle: 'Link this browser?',
        browserLabel: 'Browser',
        ipLabel: 'IP',
        warning: 'Only confirm if it\'s you with this web open. After linking, that browser can access your account until you log out.',
        cancel: 'Cancel',
        linking: 'Linking…',
        confirmYes: 'Yes, link',
        successTitle: 'Linked successfully',
        successDesc: 'The browser now has access to your account.',
        invalidCode: 'Invalid code (6 letters/numbers)',
        unknown: 'Unknown'
    },
    francés: {
        title: 'Lier le web',
        close: 'Fermer',
        scanInstructions: 'Pointe la caméra sur le QR affiché sur kaixo.ai/web',
        starting: 'Démarrage de la caméra…',
        manualBtn: 'Saisir le code manuellement',
        manualTitle: 'Saisis le code de 6 caractères',
        validating: 'Validation…',
        continue: 'Continuer',
        useCamera: '← Utiliser la caméra',
        confirmTitle: 'Lier ce navigateur ?',
        browserLabel: 'Navigateur',
        ipLabel: 'IP',
        warning: 'Confirme seulement si c\'est toi qui as cette web ouverte. Après la liaison, ce navigateur pourra accéder à ton compte jusqu\'à ta déconnexion.',
        cancel: 'Annuler',
        linking: 'Liaison…',
        confirmYes: 'Oui, lier',
        successTitle: 'Lié avec succès',
        successDesc: 'Le navigateur a maintenant accès à ton compte.',
        invalidCode: 'Code invalide (6 lettres/chiffres)',
        unknown: 'Inconnu'
    },
    alemán: {
        title: 'Web verknüpfen',
        close: 'Schließen',
        scanInstructions: 'Kamera auf den QR-Code auf kaixo.ai/web richten',
        starting: 'Kamera startet…',
        manualBtn: 'Code manuell eingeben',
        manualTitle: 'Gib den 6-Zeichen-Code ein',
        validating: 'Validierung…',
        continue: 'Weiter',
        useCamera: '← Kamera nutzen',
        confirmTitle: 'Diesen Browser verknüpfen?',
        browserLabel: 'Browser',
        ipLabel: 'IP',
        warning: 'Bestätige nur, wenn du diese Webseite geöffnet hast. Nach dem Verknüpfen kann der Browser auf dein Konto zugreifen, bis du dich abmeldest.',
        cancel: 'Abbrechen',
        linking: 'Verknüpfen…',
        confirmYes: 'Ja, verknüpfen',
        successTitle: 'Erfolgreich verknüpft',
        successDesc: 'Der Browser hat jetzt Zugriff auf dein Konto.',
        invalidCode: 'Ungültiger Code (6 Buchstaben/Zahlen)',
        unknown: 'Unbekannt'
    },
    portugués: {
        title: 'Vincular web',
        close: 'Fechar',
        scanInstructions: 'Aponta a câmara ao QR que aparece em kaixo.ai/web',
        starting: 'A iniciar câmara…',
        manualBtn: 'Introduzir código manualmente',
        manualTitle: 'Introduz o código de 6 caracteres',
        validating: 'A validar…',
        continue: 'Continuar',
        useCamera: '← Usar câmara',
        confirmTitle: 'Vincular este navegador?',
        browserLabel: 'Navegador',
        ipLabel: 'IP',
        warning: 'Só confirma se és tu que tens esta web aberta. Após vincular, esse navegador poderá aceder à tua conta até terminares sessão.',
        cancel: 'Cancelar',
        linking: 'A vincular…',
        confirmYes: 'Sim, vincular',
        successTitle: 'Vinculado com sucesso',
        successDesc: 'O navegador agora tem acesso à tua conta.',
        invalidCode: 'Código inválido (6 letras/números)',
        unknown: 'Desconhecido'
    },
    italiano: {
        title: 'Collega web',
        close: 'Chiudi',
        scanInstructions: 'Punta la fotocamera sul QR mostrato in kaixo.ai/web',
        starting: 'Avvio fotocamera…',
        manualBtn: 'Inserisci il codice manualmente',
        manualTitle: 'Inserisci il codice di 6 caratteri',
        validating: 'Convalida…',
        continue: 'Continua',
        useCamera: '← Usa fotocamera',
        confirmTitle: 'Collegare questo browser?',
        browserLabel: 'Browser',
        ipLabel: 'IP',
        warning: 'Conferma solo se sei tu ad avere aperto questo web. Dopo il collegamento, quel browser potrà accedere al tuo account finché non esci.',
        cancel: 'Annulla',
        linking: 'Collegamento…',
        confirmYes: 'Sì, collega',
        successTitle: 'Collegato con successo',
        successDesc: 'Il browser ora ha accesso al tuo account.',
        invalidCode: 'Codice non valido (6 lettere/numeri)',
        unknown: 'Sconosciuto'
    },
    euskera: {
        title: 'Web lotu',
        close: 'Itxi',
        scanInstructions: 'Apuntatu kamera kaixo.ai/web-eko QR-era',
        starting: 'Kamera abiarazten…',
        manualBtn: 'Sartu kodea eskuz',
        manualTitle: 'Sartu 6 karaktereko kodea',
        validating: 'Balidatzen…',
        continue: 'Jarraitu',
        useCamera: '← Erabili kamera',
        confirmTitle: 'Lotu nabigatzaile hau?',
        browserLabel: 'Nabigatzailea',
        ipLabel: 'IP',
        warning: 'Berretsi soilik web hau zabalik duzun zu bazara. Lotu ondoren, nabigatzaile horrek zure kontura sartu ahalko du saioa amaitu arte.',
        cancel: 'Utzi',
        linking: 'Lotzen…',
        confirmYes: 'Bai, lotu',
        successTitle: 'Ondo lotuta',
        successDesc: 'Nabigatzaileak zure kontuan sarbidea du orain.',
        invalidCode: 'Kode baliogabea (6 letra/zenbaki)',
        unknown: 'Ezezaguna'
    }
};
function pickWlsL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return WLS_I18N[u?.language] || WLS_I18N.inglés;
    } catch { return WLS_I18N.inglés; }
}

const SCAN_INTERVAL_MS = 250;

// Extrae el token de un dato leído. Acepta formatos:
//   "kaixo://link/ABC123"  → ABC123
//   "ABC123"               → ABC123 (escrito a mano)
function parseToken(raw) {
    if (!raw) return null;
    const s = String(raw).trim().toUpperCase();
    const m = s.match(/(?:KAIXO:\/\/LINK\/)?([A-Z0-9]{6})/);
    return m ? m[1] : null;
}

export default function WebLinkScanner({ onClose, onSuccess }) {
    const t = pickWlsL();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const scanTimerRef = useRef(null);

    const [starting, setStarting] = useState(true);
    const [permissionError, setPermissionError] = useState(false);
    const [manualMode, setManualMode] = useState(false);
    const [manualCode, setManualCode] = useState('');

    // Tras detección de token: pedimos /redeem y mostramos confirmación.
    const [pending, setPending] = useState(null); // { token, deviceInfo }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    // ─── Iniciar cámara ──────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        async function start() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' },
                    audio: false
                });
                if (cancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                setStarting(false);
                startScanning();
            } catch (err) {
                console.warn('[WebLinkScanner] cámara no disponible:', err.message);
                setPermissionError(true);
                setStarting(false);
            }
        }
        start();
        return () => {
            cancelled = true;
            stopScanning();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Loop de escaneo ─────────────────────────────────────────
    function startScanning() {
        stopScanning();
        scanTimerRef.current = setInterval(scanFrame, SCAN_INTERVAL_MS);
    }
    function stopScanning() {
        if (scanTimerRef.current) {
            clearInterval(scanTimerRef.current);
            scanTimerRef.current = null;
        }
    }

    function scanFrame() {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, w, h);
        let imageData;
        try {
            imageData = ctx.getImageData(0, 0, w, h);
        } catch {
            return;
        }
        const code = jsQR(imageData.data, w, h, { inversionAttempts: 'dontInvert' });
        if (code && code.data) {
            const token = parseToken(code.data);
            if (token) {
                stopScanning();
                redeem(token);
            }
        }
    }

    // ─── Redeem (paso 1) ────────────────────────────────────────
    async function redeem(token) {
        setBusy(true);
        setError('');
        try {
            const { data } = await api.post('/web-link/redeem', { token });
            setPending({ token, deviceInfo: data.deviceInfo || {} });
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || 'Error desconocido';
            setError(msg);
            // Reanudar escaneo si todavía no se cerró el modal
            setTimeout(() => {
                if (!streamRef.current) return;
                setError('');
                startScanning();
            }, 2200);
        } finally {
            setBusy(false);
        }
    }

    // ─── Confirm (paso 2) ───────────────────────────────────────
    async function confirm() {
        if (!pending) return;
        setBusy(true);
        setError('');
        try {
            await api.post('/web-link/confirm', { token: pending.token });
            setDone(true);
            setTimeout(() => {
                onSuccess && onSuccess();
                onClose && onClose();
            }, 1200);
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || 'Error desconocido';
            setError(msg);
        } finally {
            setBusy(false);
        }
    }

    async function cancelPairing() {
        if (pending) {
            // Avisar al backend que se revoca
            try { await api.post('/web-link/revoke', { token: pending.token }); } catch {}
        }
        setPending(null);
        setError('');
        if (streamRef.current) startScanning();
    }

    function handleManualSubmit(e) {
        e.preventDefault();
        const token = parseToken(manualCode);
        if (!token) {
            setError(t.invalidCode);
            return;
        }
        redeem(token);
    }

    return ReactDOM.createPortal(
        <div className="wls-overlay">
            <div className="wls-header">
                <button className="wls-close" onClick={onClose} aria-label={t.close}>✕</button>
                <h2>{t.title}</h2>
            </div>

            {!pending && !done && !manualMode && !permissionError && (
                <div className="wls-camera">
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        autoPlay
                        className="wls-video"
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    <div className="wls-overlay-frame" />
                    <div className="wls-instructions">
                        {t.scanInstructions}
                    </div>
                    {starting && <div className="wls-starting">{t.starting}</div>}
                    <button className="wls-manual-btn" onClick={() => { stopScanning(); setManualMode(true); }}>
                        {t.manualBtn}
                    </button>
                </div>
            )}

            {(permissionError || manualMode) && !pending && !done && (
                <form className="wls-manual" onSubmit={handleManualSubmit}>
                    <div className="wls-manual-title">
                        {t.manualTitle}
                    </div>
                    <input
                        type="text"
                        className="wls-manual-input"
                        value={manualCode}
                        onChange={e => setManualCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                        placeholder="ABC234"
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        maxLength={6}
                    />
                    <button type="submit" className="wls-submit" disabled={busy || manualCode.length !== 6}>
                        {busy ? t.validating : t.continue}
                    </button>
                    {error && <div className="wls-error">{error}</div>}
                    {!permissionError && (
                        <button type="button" className="wls-back-btn" onClick={() => { setManualMode(false); setError(''); if (streamRef.current) startScanning(); }}>
                            {t.useCamera}
                        </button>
                    )}
                </form>
            )}

            {pending && !done && (
                <div className="wls-confirm">
                    <div className="wls-confirm-icon">🌐</div>
                    <h3>{t.confirmTitle}</h3>
                    <div className="wls-device">
                        <div className="wls-device-label">{t.browserLabel}</div>
                        <div className="wls-device-value">
                            {shortenUserAgent(pending.deviceInfo.userAgent, t.unknown)}
                        </div>
                        {pending.deviceInfo.ip && (
                            <>
                                <div className="wls-device-label">{t.ipLabel}</div>
                                <div className="wls-device-value">{pending.deviceInfo.ip}</div>
                            </>
                        )}
                    </div>
                    <div className="wls-warning">
                        {t.warning}
                    </div>
                    {error && <div className="wls-error">{error}</div>}
                    <div className="wls-actions">
                        <button className="wls-btn-cancel" onClick={cancelPairing} disabled={busy}>
                            {t.cancel}
                        </button>
                        <button className="wls-btn-confirm" onClick={confirm} disabled={busy}>
                            {busy ? t.linking : t.confirmYes}
                        </button>
                    </div>
                </div>
            )}

            {done && (
                <div className="wls-done">
                    <div className="wls-done-icon">✓</div>
                    <h3>{t.successTitle}</h3>
                    <p>{t.successDesc}</p>
                </div>
            )}
        </div>,
        document.body
    );
}

// Acortador de userAgent para mostrar algo legible al usuario.
// Recibe un fallback localizado para "Desconocido" desde el componente.
function shortenUserAgent(ua, unknownLabel = 'Unknown') {
    if (!ua) return unknownLabel;
    if (/iPhone|iPad/i.test(ua)) return 'Safari iOS';
    if (/Android/i.test(ua)) return 'Chrome Android';
    if (/Edg\//i.test(ua)) return 'Microsoft Edge';
    if (/Chrome/i.test(ua)) return 'Google Chrome';
    if (/Firefox/i.test(ua)) return 'Mozilla Firefox';
    if (/Safari/i.test(ua)) return 'Safari';
    return ua.slice(0, 50);
}
